import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BASE_URL } from '../../config/env';
import { buildQueryParams, removeEmpty } from '../../utils/object';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_URL}/users`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.auth?.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Users'],
  endpoints: (builder) => ({
    getUsers: builder.query({
      query: ({ page, limit, projectId, user_type, search, clientId } = {}) => ({
        url: 'web',
        params: buildQueryParams({ page, limit, project_id: projectId, user_type, search, client_id: clientId }),
      }),
      providesTags: ['Users'],
    }),
    getFarmers: builder.query({
      query: ({ page, limit, projectId, search, clientId } = {}) => ({
        url: 'farmers',
        params: buildQueryParams({ page, limit, project_id: projectId, search, client_id: clientId }),
      }),
      providesTags: ['Users'],
    }),
    getUserFilters: builder.query({
      query: (args = {}) => {
        const params = removeEmpty(args);
        return {
          url: 'filters',
          ...(Object.keys(params).length > 0 && { params: params }),
        };
      },
    }),
    getFieldAgents: builder.query({
      query: ({ page, limit, projectId, search, clientId } = {}) => ({
        url: 'field-agents',
        params: buildQueryParams({ page, limit, project_id: projectId, search, client_id: clientId }),
      }),
      providesTags: ['Users'],
    }),
    getFetchUserProfile: builder.query({
      query: () => `user-profile`,
    }),
    validateSession: builder.query({
      query: () => 'validate',
    }),
    userSignIn: builder.mutation({
      query: (payload) => {
        return {
          url: 'login',
          method: 'POST',
          body: payload,
        };
      },
    }),
    getProjectByClient: builder.query({
      query: (id) => {
        return {
          url: `project-by-client/${id}`,
        };
      },
    }),
    postNewUser: builder.mutation({
      query: (payload) => {
        return {
          url: '/',
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ['Users'],
    }),
    getProjectsByUser: builder.query({
      query: (user_id) => `projects-by-user/${user_id}`,
    }),
    updateUser: builder.mutation({
      query: ({ user_id, action = 'edit', body = {} }) => ({
        url: `edit-user?action=${action}&user_id=${user_id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    changePassword: builder.mutation({
      query: (payload) => ({
        url: 'change-password',
        method: 'PATCH',
        body: payload,
      }),
    }),
    getDevice: builder.query({
      query: (user_id) => `device/${user_id}`,
      providesTags: ['Users'],
    }),
    resetDevice: builder.mutation({
      query: ({ request_id, reject }) => ({
        url: `device/request/${request_id}`,
        method: 'POST',
        body: { reject },
      }),
      invalidatesTags: ['Device', 'Users'],
    }),
    getLoginRequests: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value) queryParams.append(key, value);
          });
        }
        return `device/requests?${queryParams.toString()}`;
      },
      providesTags: ['DeviceRequests'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetFarmersQuery,
  useGetUserFiltersQuery,
  useGetFieldAgentsQuery,
  useValidateSessionQuery,
  useUserSignInMutation,
  usePostNewUserMutation,
  useGetProjectByClientQuery,
  useGetFetchUserProfileQuery,
  useGetProjectsByUserQuery,
  useUpdateUserMutation,
  useChangePasswordMutation,
  useGetDeviceQuery,
  useResetDeviceMutation,
  useGetLoginRequestsQuery,
} = userApi;
